import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  CButton,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
} from "@coreui/react";
import { Spinner } from "react-bootstrap";
import { errorAlert, successAlert } from "../utils/messages";
import { getRoutePath } from "../../routes";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import {
  ItemRequestStatus,
  createItem,
  getItem,
  updateItem,
} from "../../api/generics";
import {
  RemissionNote,
  REMISSION_NOTE_STATUS_APPROVED_BY_SET,
  REMISSION_NOTE_STATUS_UPLOADED_TO_SET,
} from "../../models/remission-note";
import {
  RemissionNoteFormProvider,
  useRemissionNoteFormContext,
} from "./context/RemissionNoteFormContext";
import { RemissionNoteFormHeader } from "./sections/RemissionNoteFormHeader";
import { RemissionNoteFormDNCPSection } from "./sections/RemissionNoteFormDNCPSection";
import { RemissionNoteFormDetailsSection } from "./sections/RemissionNoteFormDetailsSection";
import { RemissionNoteFormAssociatedDocumentSection } from "./sections/RemissionNoteFormAssociatedDocumentSection";
import { RemissionNoteFormTransportSection } from "./sections/RemissionNoteFormTransportSection";
import { RemissionNoteFormLocationsSection } from "./sections/RemissionNoteFormLocationsSection";
import { RemissionNoteFormVehicleSection } from "./sections/RemissionNoteFormVehicleSection";
import { RemissionNoteFormCarrierDriverSection } from "./sections/RemissionNoteFormCarrierDriverSection";
import { RemissionNoteFormItems } from "./sections/RemissionNoteFormItems";
import Loading from "../indicators/Loading";
import NotFound from "../indicators/NotFound";
import ServerError from "../indicators/ServerError";
import ClientUpdateModal from "../client/ClientUpdateModal";
import { getStampById } from "../../api/stamp";
import { getEstablishmentById } from "../../api/establishment";
import { getDispatchPointById } from "../../api/dispatch-point";
import { getClientForDocument } from "../../api/client";
import { getInvoiceById } from "../../api/invoice";
import {
  getCityByCode,
  getDepartmentByCode,
  getDistrictByCode,
} from "../../api/locations";
import { changeTZToPyg, changeTZToUtc } from "../../utils/dates";
import { useIdempotencyKey } from "../../hooks/use-idempotency-key";
import { ASSOCIATED_DOCUMENT_TYPE_NONE } from "../../models/associated-document";

const RemissionNoteFormContent: React.FC = () => {
  const history = useHistory();

  const getEditingRemissionNoteId = () => {
    const urlParams = new URLSearchParams(history.location.search);
    const editingRemissionNoteId = urlParams.get("id");

    if (editingRemissionNoteId !== null) {
      if (!isNaN(parseInt(editingRemissionNoteId))) {
        return parseInt(editingRemissionNoteId);
      }
    }

    return undefined;
  };

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );
  const stamps = useSelector(
    (state: RootState) => state.taxPayerParameters.data.stamps
  );
  const establishments = useSelector(
    (state: RootState) => state.taxPayerParameters.data.establishments
  );
  const dispatchPoints = useSelector(
    (state: RootState) => state.taxPayerParameters.data.dispatchPoints
  );

  const [loading, setLoading] = useState(true);
  const [loadNotFound, setLoadNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submissionLockRef = useRef(false);
  const idempotencyKey = useIdempotencyKey();
  const [showAddClient, setShowAddClient] = useState(false);

  // Access context from hook
  const context = useRemissionNoteFormContext();
  const {
    remissionNoteRef,
    setRemissionNoteDate,
    setStamp,
    setEstablishment,
    setDispatchPoint,
    setClient,
    setElectronicInvoice,
    setTransportDepartureDepartment,
    setTransportDepartureDistrict,
    setTransportDepartureCity,
    setTransportArrivalDepartment,
    setTransportArrivalDistrict,
    setTransportArrivalCity,
    setAssociatedDocumentType,
    setItems,
    setErrors,
    onStampChange,
    onEstablishmentChange,
    onDispatchPointChange,
  } = context;

  const onSave = async () => {
    if (remissionNoteRef.current === null || submissionLockRef.current) {
      return;
    }

    submissionLockRef.current = true;
    setSubmitting(true);

    let requestPromise: Promise<ItemRequestStatus<RemissionNote>>;
    let successMessage = "Nota de Remisión agregada exitósamente";

    const remissionNote = remissionNoteRef.current;
    remissionNote.taxPayerRuc = taxPayer.ruc;
    remissionNote.taxPayerSocialName = taxPayer.socialName;
    remissionNote.taxPayerFantasyName = taxPayer.fantasyName;

    // TODO: make this dynamic
    remissionNote.carrierIsTaxPayer = true;

    remissionNote.associatedDocumentType =
      remissionNote.associatedDocumentType || ASSOCIATED_DOCUMENT_TYPE_NONE;
    remissionNote.hasAssociatedDocument =
      remissionNote.associatedDocumentType !== ASSOCIATED_DOCUMENT_TYPE_NONE;

    if (remissionNote.id === undefined) {
      requestPromise = createItem<RemissionNote>(
        "/remission_notes/create/",
        remissionNote,
        { "Idempotency-Key": idempotencyKey },
      );
    } else {
      successMessage = "Nota de Remisión actualizada exitósamente";
      requestPromise = updateItem<RemissionNote>(
        `/remission_notes/${remissionNote.id}/`,
        remissionNote
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";

      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
      submissionLockRef.current = false;
    } else {
      successAlert(successMessage);
      history.push(getRoutePath("remissionNotes"));
    }
  };

  const loadInitialRemissionNoteById =
    async (): Promise<RemissionNote | null> => {
      const remissionNoteId = getEditingRemissionNoteId();

      if (remissionNoteId === undefined) {
        return null;
      }

      let requestStatus = await getItem<RemissionNote>(
        `/remission_notes/${remissionNoteId}/`
      );

      if (requestStatus.status !== SUCCESS) {
        if (requestStatus.status === NOT_FOUND) {
          setLoadNotFound(true);
        } else {
          setLoadError(true);
        }

        return null;
      }

      if (requestStatus.data === undefined || requestStatus.data === null) {
        setLoadError(true);
        return null;
      }

      return requestStatus.data!;
    };

  const loadInitialData = async () => {
    setLoading(true);
    const remissionNote = await loadInitialRemissionNoteById();

    if (remissionNote === null) {
      setLoading(false);
      return;
    }

    if (
      remissionNote.status === REMISSION_NOTE_STATUS_UPLOADED_TO_SET ||
      remissionNote.status === REMISSION_NOTE_STATUS_APPROVED_BY_SET
    ) {
      setLoading(false);
      setLoadError(true);
      return;
    }

    const stampPromise = getStampById(
      remissionNote.stampId,
      remissionNote.taxPayerId
    );
    const establishmentPromise = getEstablishmentById(
      remissionNote.establishmentId,
      remissionNote.taxPayerId
    );
    const dispatchPointPromise = getDispatchPointById(
      remissionNote.dispatchPointId,
      remissionNote.taxPayerId
    );
    const clientPromise = getClientForDocument(remissionNote);

    let invoicePromise: Promise<any | null> | undefined = undefined;

    if (remissionNote.invoiceId !== undefined) {
      invoicePromise = getInvoiceById(
        remissionNote.invoiceId,
        remissionNote.taxPayerId
      );
    }

    const transportDepartureDepartmentPromise = getDepartmentByCode(
      remissionNote.transportDepartureDepartment
    );
    const transportDepartureDistrictPromise = getDistrictByCode(
      remissionNote.transportDepartureDistrict
    );
    const transportDepartureCityPromise = getCityByCode(
      remissionNote.transportDepartureCity
    );

    const transportArrivalDepartmentPromise = getDepartmentByCode(
      remissionNote.transportArrivalDepartment
    );
    const transportArrivalDistrictPromise = getDistrictByCode(
      remissionNote.transportArrivalDistrict
    );
    const transportArrivalCityPromise = getCityByCode(
      remissionNote.transportArrivalCity
    );

    const stamp = await stampPromise;
    const establishment = await establishmentPromise;
    const dispatchPoint = await dispatchPointPromise;
    const client = await clientPromise;
    const transportDepartureDepartment =
      await transportDepartureDepartmentPromise;
    const transportDepartureDistrict = await transportDepartureDistrictPromise;
    const transportDepartureCity = await transportDepartureCityPromise;
    const transportArrivalDepartment = await transportArrivalDepartmentPromise;
    const transportArrivalDistrict = await transportArrivalDistrictPromise;
    const transportArrivalCity = await transportArrivalCityPromise;

    if (remissionNote.invoiceId !== undefined && invoicePromise !== undefined) {
      const invoice = await invoicePromise;
      setElectronicInvoice(invoice);
    }

    remissionNoteRef.current = remissionNote;

    setStamp(stamp);
    setEstablishment(establishment);
    setDispatchPoint(dispatchPoint);
    setClient(client);
    setTransportDepartureDepartment(transportDepartureDepartment);
    setTransportDepartureDistrict(transportDepartureDistrict);
    setTransportDepartureCity(transportDepartureCity);
    setTransportArrivalDepartment(transportArrivalDepartment);
    setTransportArrivalDistrict(transportArrivalDistrict);
    setTransportArrivalCity(transportArrivalCity);
    setAssociatedDocumentType(remissionNote.associatedDocumentType);
    setItems(remissionNote.items);

    if (remissionNote.remissionNoteDate) {
      const pygDate = new Date(remissionNote.remissionNoteDate);
      const year = pygDate.getUTCFullYear();
      const month = pygDate.getUTCMonth();
      const day = pygDate.getUTCDate();
      const hours = pygDate.getUTCHours();
      const minutes = pygDate.getUTCMinutes();
      const seconds = pygDate.getUTCSeconds();
      const remissionNoteDate = new Date(
        year,
        month,
        day,
        hours,
        minutes,
        seconds
      );
      setRemissionNoteDate(remissionNoteDate);
    }

    setLoading(false);
  };

  // Auto-select stamp if only one exists
  useEffect(() => {
    if (stamps.length === 1 && remissionNoteRef.current?.id === undefined) {
      onStampChange(stamps[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamps]);

  // Auto-select establishment if only one exists
  useEffect(() => {
    if (
      establishments.length === 1 &&
      remissionNoteRef.current?.id === undefined
    ) {
      onEstablishmentChange(establishments[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishments]);

  // Auto-select dispatch point if only one exists
  useEffect(() => {
    if (
      dispatchPoints.length === 1 &&
      remissionNoteRef.current?.id === undefined
    ) {
      onDispatchPointChange(dispatchPoints[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchPoints]);

  useEffect(() => {
    const newRemissionNoteDate = changeTZToPyg(new Date());
    if (remissionNoteRef.current !== null) {
      remissionNoteRef.current.remissionNoteDate =
        changeTZToUtc(newRemissionNoteDate).toISOString();
      setRemissionNoteDate(newRemissionNoteDate);
    }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddClientSuccess = () => {
    setShowAddClient(false);
  };

  if (loading) {
    return <Loading />;
  }

  if (loadNotFound) {
    return <NotFound />;
  }

  if (loadError) {
    return <ServerError />;
  }

  const title = getEditingRemissionNoteId()
    ? "Editar Nota de Remisión"
    : "Agregar Nota de Remisión";

  return (
    <>
      <CCard className="shadow-sm border-0">
        <CCardHeader
          style={{
            background: "linear-gradient(180deg, #006657 0%, #095e55 100%)",
            color: "white",
            fontWeight: 600,
            fontSize: "1rem",
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i className="fa fa-file-text-o" style={{ fontSize: "1rem" }}></i>
          {title}
        </CCardHeader>
        <CCardBody>
          <RemissionNoteFormHeader
            onAddClientClick={() => setShowAddClient(true)}
          />
          <RemissionNoteFormDNCPSection />
          <RemissionNoteFormDetailsSection />
          <RemissionNoteFormAssociatedDocumentSection />
          <RemissionNoteFormTransportSection />
          <RemissionNoteFormLocationsSection />
          <RemissionNoteFormVehicleSection />
          <RemissionNoteFormCarrierDriverSection />
          <RemissionNoteFormItems />
        </CCardBody>
        <CCardFooter
          style={{
            background: "var(--theme-surface-raised)",
            borderTop: "1px solid var(--theme-divider)",
            padding: "14px 20px",
          }}
        >
          <CButton
            type="submit"
            color="primary"
            onClick={onSave}
            disabled={submitting}
            style={{ minWidth: 130, fontWeight: 600 }}
          >
            {submitting ? (
              <Spinner
                animation="grow"
                style={{
                  height: "17px",
                  width: "17px",
                  marginTop: "auto",
                  marginBottom: "auto",
                  marginRight: "10px",
                }}
              />
            ) : (
              <i className="fa fa-save mr-2"></i>
            )}
            {submitting ? "Guardando..." : "Guardar"}
          </CButton>
        </CCardFooter>
      </CCard>
      <ClientUpdateModal
        show={showAddClient}
        onSuccess={onAddClientSuccess}
        onCancel={() => setShowAddClient(false)}
      />
    </>
  );
};

const RemissionNoteForm: React.FC = () => {
  const taxPayerId = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer.id
  );

  return (
    <RemissionNoteFormProvider taxPayerId={taxPayerId}>
      <RemissionNoteFormContent />
    </RemissionNoteFormProvider>
  );
};

export default RemissionNoteForm;
